"use client";

import {
  carouselClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import React, { useEffect, useMemo, useRef, useState } from "react";

export interface Faculty4MetaItem {
  id?: string | number;
  priority?: number | string;
  status?: boolean;
  metaImg?: string;
  altImg?: string;
  metaTitle?: string;
  metaDescription?: string;
}

export interface Faculty4Data {
  id?: string;
  status?: boolean;
  title?: string;
  title_text_color?: string;
  title_1_color?: string;
  background_color?: string;
  cta_button_color?: string;
  cta_text_color?: string;
  metadata?: Faculty4MetaItem[];
}

interface Faculty4Props {
  data: Faculty4Data | { data: Faculty4Data };
}

function resolvePayload(
  data: Faculty4Props["data"],
): Faculty4Data | undefined {
  const anyData = data as { data?: Faculty4Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as Faculty4Data;
  }
  return data as Faculty4Data;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** `{highlight}` / `{{line}}` → span with admin-chosen highlight color (inline
 *  style so arbitrary hex values bypass Tailwind JIT). */
function formatTitleWithHighlight(title: string, highlightColor: string): string {
  if (!title) return "";
  const safeColor = highlightColor.replace(/[^#a-zA-Z0-9(),.%\s]/g, "");
  const openTag = `<span style="color:${safeColor}">`;

  if (title.includes("{{")) {
    return title
      .replace(/\{\{([^}]+)\}\}/g, (_m, inner) => `${openTag}${escapeHtml(inner)}</span><br/>`)
      .replace(/\s+/g, " ");
  }

  if (title.includes("{")) {
    return title
      .replace(/\{/g, openTag)
      .replace(/\}/g, "</span>")
      .replace(/\s+/g, " ");
  }

  return escapeHtml(title);
}

const Faculty4 = ({ data }: Faculty4Props) => {
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const {
    status = true,
    title = "",
    title_text_color = "#FFFFFF",
    title_1_color = "#FFD700",
    background_color = "#291973",
    cta_button_color = "#E36E26",
    cta_text_color = "#FFFFFF",
    metadata = [],
  } = sectionData || {};

  const items = useMemo(() => {
    if (!Array.isArray(metadata) || metadata.length === 0) return [];
    return [...metadata]
      .filter((m) => m && (m.status === undefined || m.status === true))
      .sort(
        (a, b) =>
          Number(a.priority ?? 0) - Number(b.priority ?? 0),
      );
  }, [metadata]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status && items.length > 0) {
  //     FireWidgetViewedEvent(ref, "faculty_4");
  //   }
  // }, [status, items.length]);

  // Keep currentIndex in range if the items list shrinks.
  useEffect(() => {
    if (currentIndex > items.length - 1) {
      setCurrentIndex(Math.max(0, items.length - 1));
    }
  }, [items.length, currentIndex]);

  if (!status || items.length === 0) return null;

  const isAtStart = currentIndex === 0;
  const isAtEnd = currentIndex >= items.length - 1;

  const handlePrev = () => {
    if (isAtStart) return;
    setCurrentIndex((i) => i - 1);
    carouselClick({ section: "faculty_4", label: "left" });
  };
  const handleNext = () => {
    if (isAtEnd) return;
    setCurrentIndex((i) => i + 1);
    carouselClick({ section: "faculty_4", label: "right" });
  };

  return (
    <section
      ref={ref}
      data-faculty="4"
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "flex w-full items-center justify-center",
      )}
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      {/* Constrain carousel width like the iitkgp page does at its page-level
          max-width wrapper — without this the card stretches to the viewport
          on the campus-portal preview where the parent has no max-width cap. */}
      <div
        className={cn(
          "relative mx-auto w-full max-w-screen-container p-0",
          "md:px-10 md:py-10",
        )}
      >
        <div
          className={cn(
            "relative w-full overflow-hidden rounded-none md:rounded-[20px]",
          )}
          style={{ backgroundColor: background_color }}
        >
          <div
            className="flex transition-transform duration-[1200ms]"
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
            }}
          >
            {items.map((item, idx) => (
              <div
                key={item.id ?? `card-${idx}`}
                className="w-full flex-shrink-0 bg-transparent"
              >
                <Faculty4Card
                  title={title}
                  titleColor={title_text_color}
                  titleHighlightColor={title_1_color}
                  item={item}
                />
              </div>
            ))}
          </div>
        </div>

        {items.length > 1 ? (
          <div
            className={cn(
              "absolute -translate-y-1/2 flex justify-between pointer-events-none",
              "top-[68%] left-0 right-0 px-[10px]",
              "md:top-1/2 md:left-6 md:right-6 md:px-0",
            )}
          >
            <button
              type="button"
              aria-label="Previous card"
              onClick={handlePrev}
              disabled={isAtStart}
              className={cn(
                "pointer-events-auto flex size-[50px] md:size-[62px] items-center justify-center rounded-full shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] transition-colors",
                isAtStart && "cursor-default",
              )}
              style={
                isAtStart
                  ? { backgroundColor: "#FFFFFF", color: "#000000" }
                  : { backgroundColor: cta_button_color, color: cta_text_color }
              }
            >
              <ChevronLeft className="size-6" strokeWidth={2.5} />
            </button>
            <button
              type="button"
              aria-label="Next card"
              onClick={handleNext}
              disabled={isAtEnd}
              className={cn(
                "pointer-events-auto flex size-[50px] md:size-[62px] items-center justify-center rounded-full shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] transition-colors",
                isAtEnd && "cursor-default",
              )}
              style={
                isAtEnd
                  ? { backgroundColor: "#FFFFFF", color: "#000000" }
                  : { backgroundColor: cta_button_color, color: cta_text_color }
              }
            >
              <ChevronRight className="size-6" strokeWidth={2.5} />
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
};

interface Faculty4CardProps {
  title: string;
  titleColor: string;
  titleHighlightColor: string;
  item: Faculty4MetaItem;
}

/** Mirrors IITKGP AcademicsCard: shared title on top + per-slide image / name
 *  / description. Card itself is transparent (the carousel container provides
 *  the background color). */
const Faculty4Card = ({
  title,
  titleColor,
  titleHighlightColor,
  item,
}: Faculty4CardProps) => {
  const {
    metaTitle = "",
    metaDescription = "",
    metaImg = "",
    altImg = "academics",
  } = item;

  return (
    <div
      className={cn(
        "flex items-center justify-center bg-transparent",
        "py-10 px-8 md:pl-6 md:pr-8",
      )}
      style={{ color: titleColor }}
    >
      <div className="flex w-full flex-col gap-[26px] md:gap-7 lg:w-[834px]">
        {title ? (
          <h2
            className="text-center text-[28px] font-semibold leading-tight md:text-[32px]"
            style={{ color: titleColor }}
            dangerouslySetInnerHTML={{
              __html: formatTitleWithHighlight(title, titleHighlightColor),
            }}
          />
        ) : null}
        <div className="flex flex-col items-center gap-[26px] md:flex-row md:gap-11">
          {metaImg ? (
            <div className="relative h-[220px] w-[230px] shrink-0">
              <Image
                src={metaImg}
                alt={altImg || metaTitle || "academics"}
                fill
                className="rounded-[13px] object-cover"
                sizes="230px"
              />
            </div>
          ) : null}
          <div className="flex w-[234px] flex-col gap-2 text-center md:w-full md:text-left">
            {metaTitle ? (
              <p className="text-[20px] font-semibold leading-snug md:text-[24px]">
                {metaTitle}
              </p>
            ) : null}
            {metaDescription ? (
              <p className="h-[200px] overflow-y-auto text-[14px] leading-relaxed md:h-full md:text-[16px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {metaDescription}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Faculty4;
